import React, { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Alert
} from '@mui/material';
import api from '../../services/api';
import { formatCurrency } from '../../utils/currency';

interface TopCustomer {
    _id: string;
    name: string;
    totalSpent: number;
    orderCount: number;
}

const TopCustomersWidget: React.FC = () => {
    const [customers, setCustomers] = useState<TopCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await api.get('/admin/reports/top-customers?limit=5');
                setCustomers(response.data.data);
            } catch (err: any) {
                console.error('Failed to load top customers', err);
                setError(err.response?.data?.message || 'Failed to load top customers');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, paddingBottom: '8px !important' }}>
                <Typography variant="h6" component="div" gutterBottom>
                    Top Customers
                </Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : customers.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <Typography variant="body2" color="text.secondary">
                            No customer data available yet
                        </Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>Rank</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Spent</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Orders</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customers.map((customer, index) => (
                                <TableRow key={customer._id || `guest-${index}`} hover>
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        color: index === 0 ? 'warning.main' : 'text.secondary'
                                    }}>
                                        #{index + 1}
                                    </TableCell>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                        {customer.name}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                        {formatCurrency(customer.totalSpent)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {customer.orderCount}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

export default TopCustomersWidget;
